import React, { useEffect, useState } from "react";

import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";

import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaImages,
  FaVideo,
  FaMapMarkerAlt,
  FaTimes,
} from "react-icons/fa";

import { db } from "../../../firebase/firebase";

import "./Gallery.css";

const Gallery = () => {
  // =====================================================
  // STATE
  // =====================================================

  const [sites, setSites] = useState([]);

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [showModal, setShowModal] = useState(false);

  const [editingSite, setEditingSite] = useState(null);

  const [siteName, setSiteName] = useState("");

  const [location, setLocation] = useState("");

  const [state, setState] = useState("");

  const [description, setDescription] = useState("");

  const [images, setImages] = useState([]);

  const [videos, setVideos] = useState([]);

  // =====================================================
  // LOAD GALLERY SITES
  // =====================================================

  const loadSites = async () => {
    try {
      setLoading(true);

      const snapshot = await getDocs(collection(db, "gallerySites"));

      const gallerySites = snapshot.docs.map((item) => ({
        id: item.id,
        ...item.data(),
      }));

      setSites(gallerySites);
    } catch (error) {
      console.error("Gallery loading error:", error);

      alert(`Failed to load gallery sites.\n\n${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // INITIAL LOAD
  // =====================================================

  useEffect(() => {
    loadSites();
  }, []);

  // =====================================================
  // OPEN ADD MODAL
  // =====================================================

  const openAddModal = () => {
    setEditingSite(null);

    setSiteName("");

    setLocation("");

    setState("");

    setDescription("");

    setImages([]);

    setVideos([]);

    setShowModal(true);
  };

  // =====================================================
  // OPEN EDIT MODAL
  // =====================================================

  const openEditModal = (site) => {
    setEditingSite(site);

    setSiteName(site.siteName || "");

    setLocation(site.location || "");

    setState(site.state || "");

    setDescription(site.description || "");

    setImages(Array.isArray(site.images) ? site.images : []);

    setVideos(Array.isArray(site.videos) ? site.videos : []);

    setShowModal(true);
  };

  // =====================================================
  // CLOSE MODAL
  // =====================================================

  const closeModal = () => {
    if (saving) {
      return;
    }

    setShowModal(false);

    setEditingSite(null);
  };

  // =====================================================
  // CLOUDINARY UPLOAD
  // =====================================================

  const uploadToCloudinary = async (file, type) => {
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;

    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

    if (!cloudName) {
      throw new Error("REACT_APP_CLOUDINARY_CLOUD_NAME is missing.");
    }

    if (!uploadPreset) {
      throw new Error("REACT_APP_CLOUDINARY_UPLOAD_PRESET is missing.");
    }

    const formData = new FormData();

    formData.append("file", file);

    formData.append("upload_preset", uploadPreset);

    const resourceType = type === "video" ? "video" : "image";

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudName}/${resourceType}/upload`,
      {
        method: "POST",
        body: formData,
      },
    );

    const data = await response.json();

    if (!response.ok) {
      console.error("Cloudinary error:", data);

      throw new Error(data.error?.message || `Failed to upload ${type}.`);
    }

    return {
      url: data.secure_url,

      publicId: data.public_id,

      type,
    };
  };

  // =====================================================
  // IMAGE SELECT
  // =====================================================

  const handleImageSelect = async (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      return;
    }

    try {
      setSaving(true);

      const uploadedImages = [];

      for (const file of files) {
        if (!file.type.startsWith("image/")) {
          throw new Error(`${file.name} is not a valid image.`);
        }

        const uploaded = await uploadToCloudinary(file, "image");

        uploadedImages.push(uploaded);
      }

      setImages((previous) => [...previous, ...uploadedImages]);
    } catch (error) {
      console.error("Image upload error:", error);

      alert(error.message || "Image upload failed.");
    } finally {
      setSaving(false);

      event.target.value = "";
    }
  };

  // =====================================================
  // VIDEO SELECT
  // =====================================================

  const handleVideoSelect = async (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) {
      return;
    }

    try {
      setSaving(true);

      const uploadedVideos = [];

      for (const file of files) {
        if (!file.type.startsWith("video/")) {
          throw new Error(`${file.name} is not a valid video.`);
        }

        const uploaded = await uploadToCloudinary(file, "video");

        uploadedVideos.push(uploaded);
      }

      setVideos((previous) => [...previous, ...uploadedVideos]);
    } catch (error) {
      console.error("Video upload error:", error);

      alert(error.message || "Video upload failed.");
    } finally {
      setSaving(false);

      event.target.value = "";
    }
  };

  // =====================================================
  // REMOVE IMAGE
  // =====================================================

  const removeImage = (index) => {
    setImages((previous) =>
      previous.filter((_, imageIndex) => imageIndex !== index),
    );
  };

  // =====================================================
  // REMOVE VIDEO
  // =====================================================

  const removeVideo = (index) => {
    setVideos((previous) =>
      previous.filter((_, videoIndex) => videoIndex !== index),
    );
  };

  // =====================================================
  // SAVE GALLERY SITE
  // =====================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!siteName.trim()) {
      alert("Please enter the site name.");

      return;
    }

    if (!location.trim()) {
      alert("Please enter the location.");

      return;
    }

    try {
      setSaving(true);

      const galleryData = {
        siteName: siteName.trim(),

        location: location.trim(),

        state: state.trim(),

        description: description.trim(),

        images: images,

        videos: videos,

        updatedAt: serverTimestamp(),
      };

      // =================================================
      // UPDATE EXISTING SITE
      // =================================================

      if (editingSite) {
        const siteRef = doc(db, "gallerySites", editingSite.id);

        await updateDoc(siteRef, galleryData);

        alert("Gallery site updated successfully.");
      }

      // =================================================
      // CREATE NEW SITE
      // =================================================
      else {
        await addDoc(collection(db, "gallerySites"), {
          ...galleryData,

          createdAt: serverTimestamp(),
        });

        alert("Gallery site added successfully.");
      }

      setShowModal(false);

      setEditingSite(null);

      await loadSites();
    } catch (error) {
      console.error("Save gallery site error:", error);

      if (error.code === "permission-denied") {
        alert(
          "Firebase permission denied.\n\n" +
            "Your Firestore Security Rules are blocking gallerySites.\n\n" +
            "Please update your Firestore Rules.",
        );
      } else {
        alert(error.message || "Failed to save gallery site.");
      }
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // DELETE SITE
  // =====================================================

  const deleteSite = async (site) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${site.siteName}"?`,
    );

    if (!confirmed) {
      return;
    }

    try {
      await deleteDoc(doc(db, "gallerySites", site.id));

      alert("Gallery site deleted successfully.");

      await loadSites();
    } catch (error) {
      console.error("Delete gallery error:", error);

      if (error.code === "permission-denied") {
        alert(
          "Firebase permission denied. Your Firestore Rules are blocking gallery deletion.",
        );
      } else {
        alert(error.message || "Failed to delete gallery site.");
      }
    }
  };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {
    return (
      <div className="admin-gallery-page">
        <div className="admin-gallery-loading">Loading Gallery...</div>
      </div>
    );
  }

  // =====================================================
  // PAGE
  // =====================================================

  return (
    <div className="admin-gallery-page">
      {/* =================================================
          HEADER
      ================================================= */}

      <div className="admin-gallery-header">
        <div>
          <span className="admin-gallery-label">MEDIA MANAGEMENT</span>

          <h1>Gallery</h1>

          <p>Manage independent project sites, images and videos.</p>
        </div>

        <button className="admin-gallery-add-btn" onClick={openAddModal}>
          <FaPlus />
          Add Gallery Site
        </button>
      </div>

      {/* =================================================
          SITE GRID
      ================================================= */}

      {sites.length > 0 ? (
        <div className="admin-gallery-grid">
          {sites.map((site) => (
            <div className="admin-gallery-card" key={site.id}>
              {/* COVER */}

              <div className="admin-gallery-cover">
                {site.images?.length > 0 ? (
                  <img src={site.images[0].url} alt={site.siteName} />
                ) : (
                  <div className="admin-gallery-no-image">
                    <FaImages />
                  </div>
                )}

                <div className="admin-gallery-media-count">
                  <span>
                    <FaImages />

                    {site.images?.length || 0}
                  </span>

                  <span>
                    <FaVideo />

                    {site.videos?.length || 0}
                  </span>
                </div>
              </div>

              {/* CONTENT */}

              <div className="admin-gallery-card-content">
                <h3>{site.siteName}</h3>

                <p className="admin-gallery-location">
                  <FaMapMarkerAlt />

                  {site.location}

                  {site.state ? `, ${site.state}` : ""}
                </p>

                {site.description && (
                  <p className="admin-gallery-description">
                    {site.description}
                  </p>
                )}

                <div className="admin-gallery-actions">
                  <button
                    className="admin-gallery-edit"
                    onClick={() => openEditModal(site)}
                  >
                    <FaEdit />
                    Edit
                  </button>

                  <button
                    className="admin-gallery-delete"
                    onClick={() => deleteSite(site)}
                  >
                    <FaTrash />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="admin-gallery-empty">
          <FaImages />

          <h2>No Gallery Sites</h2>

          <p>Add your first sports project gallery site.</p>

          <button onClick={openAddModal}>
            <FaPlus />
            Add Gallery Site
          </button>
        </div>
      )}

      {/* =================================================
          MODAL
      ================================================= */}

      {showModal && (
        <div className="admin-gallery-modal">
          <div className="admin-gallery-modal-content">
            {/* HEADER */}

            <div className="admin-gallery-modal-header">
              <div>
                <span>GALLERY SITE</span>

                <h2>
                  {editingSite ? "Edit Gallery Site" : "Add Gallery Site"}
                </h2>
              </div>

              <button type="button" onClick={closeModal} disabled={saving}>
                <FaTimes />
              </button>
            </div>

            {/* FORM */}

            <form onSubmit={handleSubmit}>
              {/* SITE NAME */}

              <div className="admin-gallery-field">
                <label>Site Name *</label>

                <input
                  type="text"
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder="Example: ABC Sports Academy"
                  disabled={saving}
                />
              </div>

              {/* LOCATION */}

              <div className="admin-gallery-two-columns">
                <div className="admin-gallery-field">
                  <label>Location / City *</label>

                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Hyderabad"
                    disabled={saving}
                  />
                </div>

                <div className="admin-gallery-field">
                  <label>State</label>

                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    placeholder="Telangana"
                    disabled={saving}
                  />
                </div>
              </div>

              {/* DESCRIPTION */}

              <div className="admin-gallery-field">
                <label>Description</label>

                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe this sports site..."
                  rows="4"
                  disabled={saving}
                />
              </div>

              {/* =================================================
                  IMAGES
              ================================================= */}

              <div className="admin-gallery-upload-section">
                <div className="admin-gallery-upload-heading">
                  <div>
                    <h3>
                      <FaImages />
                      Site Images
                    </h3>

                    <p>Upload multiple images of this site.</p>
                  </div>

                  <label className="admin-gallery-upload-btn">
                    <FaPlus />
                    Add Images
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/jpg"
                      multiple
                      onChange={handleImageSelect}
                      disabled={saving}
                    />
                  </label>
                </div>

                {images.length > 0 && (
                  <div className="admin-gallery-preview-grid">
                    {images.map((image, index) => (
                      <div
                        className="admin-gallery-preview"
                        key={`${image.publicId || image.url}-${index}`}
                      >
                        <img src={image.url} alt={`Gallery ${index + 1}`} />

                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          disabled={saving}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* =================================================
                  VIDEOS
              ================================================= */}

              <div className="admin-gallery-upload-section">
                <div className="admin-gallery-upload-heading">
                  <div>
                    <h3>
                      <FaVideo />
                      Site Videos
                    </h3>

                    <p>Upload videos of this sports site.</p>
                  </div>

                  <label className="admin-gallery-upload-btn">
                    <FaPlus />
                    Add Videos
                    <input
                      type="file"
                      accept="video/mp4,video/webm,video/ogg,video/quicktime"
                      multiple
                      onChange={handleVideoSelect}
                      disabled={saving}
                    />
                  </label>
                </div>

                {videos.length > 0 && (
                  <div className="admin-gallery-video-grid">
                    {videos.map((video, index) => (
                      <div
                        className="admin-gallery-video-preview"
                        key={`${video.publicId || video.url}-${index}`}
                      >
                        <video src={video.url} controls preload="metadata" />

                        <button
                          type="button"
                          onClick={() => removeVideo(index)}
                          disabled={saving}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ACTIONS */}

              <div className="admin-gallery-modal-actions">
                <button
                  type="button"
                  className="admin-gallery-cancel"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="admin-gallery-save"
                  disabled={saving}
                >
                  {saving
                    ? "Saving..."
                    : editingSite
                      ? "Update Gallery"
                      : "Save Gallery"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
